import {
  FormField,
  FormItem,
  FormMessage,
} from "ui/components/form";
import { Form } from "react-crud-form";
import { FormLabel } from "components/form";
import { Input } from "ui/components/input";
import React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/components/select";
import { pullRequestSchema } from "./schema";
import { useCreatePullRequest } from "@/modules/pull-requests/create/mutation";
import { useUpdatePullRequest } from "@/modules/pull-requests/update/mutation";
import { useForm } from "react-hook-form";
import { useHotkeys } from "react-hotkeys-hook";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { fetchPullRequest } from "@/modules/pull-requests/form/read";
import { FormActions } from "components/form/actions";
import { eventBus } from "utils/eventBus";

const PullRequestForm = ({ handleClose, pullRequest = null }) => {
  const navigate = useNavigate();

  const createPullRequestMutation = useCreatePullRequest();
  const updatePullRequestMutation = useUpdatePullRequest();

  const form = useForm({
    resolver: zodResolver(pullRequestSchema),
  });

  async function handleCreate(context) {
    const values = { ...context.values };
    const response = await createPullRequestMutation.mutateAsync({ values });
    navigate(`/apps/pull-requests/${response.id}/edit`);
    eventBus.emit("refresh:list", `pull_requestspull-requests-${user.id}`);
  }

  async function handleUpdate(id, context) {
    const values = { ...context.dirtyValues };
    const response = await updatePullRequestMutation.mutateAsync({
      pullRequestId: id,
      values,
    });
    eventBus.emit("refresh:list", `pull_requestspull-requests-${user.id}`);
    return response;
  }

  const status = form.getValues("status");

  return (
    <Form
      form={form}
      create={handleCreate}
      update={handleUpdate}
      read={fetchPullRequest}
      itemId={pullRequest || "+"}
      schema={pullRequestSchema}
    >
      {({
        isCreating,
        createItem,
        isNewItem,
        isUpdating,
        updateItem,
        isDirty,
      }) => {
        useHotkeys(
          "ctrl+s, cmd+s",
          (event) => {
            event.preventDefault();
            if (!isCreating && isNewItem) {
              createItem().then(handleClose);
            }
            if (!isUpdating && !isNewItem) {
              updateItem().then(handleClose);
            }
          },
          { preventDefault: true }
        );

        return (
          <div className="space-y-6 max-w-md">
            <FormField
              control={form.control}
              name="url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required={true}>URL</FormLabel>
                  <Input
                    {...field}
                    type="url"
                    placeholder="https://github.com/user/repo/pull/123"
                    disabled={!isNewItem}
                  />

                  <FormMessage />
                </FormItem>
              )}
            />

            {!isNewItem && status && (
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Input
                      {...field}
                      type="text"
                      disabled={!isNewItem}
                      className="capitalize"
                    />

                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {!isNewItem && (status === "open" || status === "pending") && (
              <FormField
                control={form.control}
                name="action"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormLabel>Action</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        {...field}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue
                            className="w-full"
                            placeholder="Select"
                          />
                        </SelectTrigger>

                        <SelectContent>
                          <SelectItem value="merge">Merge</SelectItem>
                          <SelectItem value="close">Close</SelectItem>
                        </SelectContent>
                      </Select>

                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            )}

            <FormActions
              isNewItem={isNewItem}
              isCreating={isCreating}
              isUpdating={isUpdating}
              isDirty={isDirty}
              createItem={createItem}
              updateItem={updateItem}
              onCancel={handleClose}
            />
          </div>
        );
      }}
    </Form>
  );
};

export default PullRequestForm;
